import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, TrendingUp, TrendingDown, Plus, Trash2, Filter, Download } from 'lucide-react';
import RupiahIcon from '@/components/RupiahIcon';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FinanceEntry = () => {
  const { propertyName, ownerEmail } = useParams();
  const navigate = useNavigate();
  
  const [property, setProperty] = useState(null);
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    transaction_date: new Date().toISOString().split('T')[0],
    transaction_type: 'income',
    category: 'rent_payment',
    room_number: '',
    room_type: '',
    description: '',
    amount: '',
    payment_method: 'cash'
  });
  
  // Filter states
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterRoom, setFilterRoom] = useState('');
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const incomeCategories = [
    { value: 'rent_payment', label: 'Pembayaran Sewa' },
    { value: 'deposit_received', label: 'Deposit Diterima' },
    { value: 'other_income', label: 'Pemasukan Lain' }
  ];

  const expenseCategories = [
    { value: 'deposit_refund', label: 'Pengembalian Deposit' },
    { value: 'maintenance', label: 'Perbaikan/Maintenance' },
    { value: 'utilities', label: 'Listrik/Air/Internet' },
    { value: 'cleaning', label: 'Cleaning/Laundry' },
    { value: 'security', label: 'Security' },
    { value: 'other_expense', label: 'Pengeluaran Lain' }
  ];

  useEffect(() => {
    fetchData();
    // Get user role from localStorage
    const ownerData = localStorage.getItem('owner');
    if (ownerData) {
      const parsed = JSON.parse(ownerData);
      setUserRole(parsed.role);
    }
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const [propertyRes, transactionsRes, roomsRes, roomTypesRes, summaryRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/properties`, config),
        axios.get(`${BACKEND_URL}/api/admin/finance/${propertyName}?owner_email=${ownerEmail}`, config),
        axios.get(`${BACKEND_URL}/api/admin/rooms/${propertyName}?owner_email=${ownerEmail}`, config),
        axios.get(`${BACKEND_URL}/api/admin/room-types/${propertyName}?owner_email=${ownerEmail}`, config),
        axios.get(`${BACKEND_URL}/api/admin/finance-summary/${propertyName}?owner_email=${ownerEmail}`, config)
      ]);
      
      const prop = propertyRes.data.find(p => p.name === propertyName && p.owner_email === ownerEmail);
      setProperty(prop);
      setTransactions(transactionsRes.data);
      setRooms(roomsRes.data);
      setRoomTypes(roomTypesRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.post(
        `${BACKEND_URL}/api/admin/finance?owner_email=${ownerEmail}`,
        {
          ...formData,
          property_name: propertyName,
          amount: parseFloat(formData.amount),
          room_number: (formData.room_number && formData.room_number !== 'none') ? formData.room_number : null,
          room_type: formData.room_type || null
        },
        config
      );

      setSuccess('Transaksi berhasil ditambahkan!');
      fetchData();
      
      // Reset form
      setFormData({
        transaction_date: new Date().toISOString().split('T')[0],
        transaction_type: 'income',
        category: 'rent_payment',
        room_number: '',
        room_type: '',
        description: '',
        amount: '',
        payment_method: 'cash'
      });
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Gagal menambahkan transaksi');
    }
  };

  const handleFilter = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const params = new URLSearchParams();
      params.append('owner_email', ownerEmail);
      if (filterStartDate) params.append('start_date', filterStartDate);
      if (filterEndDate) params.append('end_date', filterEndDate);
      if (filterType && filterType !== 'all') params.append('transaction_type', filterType);
      if (filterCategory && filterCategory !== 'all') params.append('category', filterCategory);
      if (filterRoom && filterRoom !== 'all') params.append('room_number', filterRoom);

      const response = await axios.get(
        `${BACKEND_URL}/api/admin/finance/${propertyName}?${params.toString()}`,
        config
      );
      
      setTransactions(response.data);
    } catch (error) {
      console.error('Error filtering:', error);
    }
  };

  const handleDelete = async (transaction) => {
    if (!window.confirm('Hapus transaksi ini?')) return;

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.delete(
        `${BACKEND_URL}/api/admin/finance/${transaction.transaction_date}/${transaction.property_name}/${transaction.amount}?owner_email=${ownerEmail}`,
        config
      );
      
      setSuccess('Transaksi berhasil dihapus!');
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Gagal menghapus transaksi');
    }
  };

  const handleRoomChange = (roomNumber) => {
    setFormData({ ...formData, room_number: roomNumber });
    
    if (roomNumber && roomNumber !== 'none') {
      const room = rooms.find(r => r.room_number === roomNumber);
      if (room) {
        setFormData(prev => ({ ...prev, room_type: room.room_type }));
      }
    } else {
      setFormData(prev => ({ ...prev, room_type: '' }));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getCategoryLabel = (category, type) => {
    const categories = type === 'income' ? incomeCategories : expenseCategories;
    return categories.find(c => c.value === category)?.label || category;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-isk-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-isk-teal border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-isk-muted">Memuat data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-isk-bg">
      {/* Header */}
      <div className="glass-nav border-b border-white/20">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Button
            onClick={() => navigate('/admin')}
            variant="outline"
            className="mb-4 border-isk-teal/30 text-isk-teal hover:bg-isk-teal hover:text-white rounded-full"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Kembali ke Properties
          </Button>
          
          <div>
            <h1 className="text-3xl font-bold text-isk-text">{property?.name}</h1>
            <p className="text-isk-muted mt-1">{property?.location} • Owner: {ownerEmail}</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 p-4 rounded-xl bg-green-50 border border-green-200 text-green-700">
            {success}
          </div>
        )}
        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700">
            {error}
          </div>
        )}

        {/* Financial Summary Cards - Only visible for superadmin */}
        {summary && userRole === 'superadmin' && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="glass-card rounded-2xl p-6" data-testid="summary-income">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
              </div>
              <p className="text-sm text-isk-muted mb-1">Total Pemasukan</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(summary.total_income)}</p>
            </div>

            <div className="glass-card rounded-2xl p-6" data-testid="summary-expense">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center">
                  <TrendingDown className="w-6 h-6 text-red-600" />
                </div>
              </div>
              <p className="text-sm text-isk-muted mb-1">Total Pengeluaran</p>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(summary.total_expense)}</p>
            </div>

            <div className="glass-card rounded-2xl p-6" data-testid="summary-net">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-isk-teal/10 flex items-center justify-center">
                  <RupiahIcon className="w-6 h-6 text-isk-teal" />
                </div>
              </div>
              <p className="text-sm text-isk-muted mb-1">Pendapatan Bersih</p>
              <p className="text-2xl font-bold text-isk-teal">{formatCurrency(summary.net_income)}</p>
            </div>

            <div className="glass-card rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-yellow-500/10 flex items-center justify-center">
                  <RupiahIcon className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
              <p className="text-sm text-isk-muted mb-1">Total Deposit</p>
              <p className="text-2xl font-bold text-yellow-600">{formatCurrency(summary.total_deposit || 0)}</p>
              <p className="text-xs text-isk-muted mt-1">Kewajiban dikembalikan</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Transaction Form */}
          <div className="lg:col-span-1">
            <div className="glass-card rounded-2xl p-6 sticky top-6">
              <h3 className="text-xl font-bold text-isk-text mb-6 flex items-center">
                <Plus className="w-5 h-5 mr-2" />
                Input Transaksi
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="transaction-form">
                <div className="space-y-2">
                  <Label htmlFor="date">Tanggal *</Label>
                  <Input
                    id="date"
                    type="date"
                    required
                    value={formData.transaction_date}
                    onChange={(e) => setFormData({ ...formData, transaction_date: e.target.value })}
                    className="glass-input border-2 focus:border-isk-teal rounded-xl"
                    data-testid="input-date"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Jenis Transaksi *</Label>
                  <Select
                    value={formData.transaction_type}
                    onValueChange={(value) => {
                      setFormData({
                        ...formData,
                        transaction_type: value,
                        category: value === 'income' ? 'rent_payment' : 'maintenance'
                      });
                    }}
                  >
                    <SelectTrigger className="glass-input border-2 focus:border-isk-teal rounded-xl" data-testid="select-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="income">Pemasukan (Income)</SelectItem>
                      <SelectItem value="expense">Pengeluaran (Expense)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Kategori *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger className="glass-input border-2 focus:border-isk-teal rounded-xl" data-testid="select-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(formData.transaction_type === 'income' ? incomeCategories : expenseCategories).map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Room/Unit (Opsional)</Label>
                  <Select
                    value={formData.room_number}
                    onValueChange={handleRoomChange}
                  >
                    <SelectTrigger className="glass-input border-2 focus:border-isk-teal rounded-xl" data-testid="select-room">
                      <SelectValue placeholder="Pilih room atau kosongkan" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Tidak ada room (transaksi umum)</SelectItem>
                      {rooms.map(room => (
                        <SelectItem key={room.room_number} value={room.room_number}>
                          {room.room_number} - {room.room_type} ({room.status})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {formData.room_type && (
                  <div className="p-3 rounded-xl bg-isk-teal/5 border border-isk-teal/20">
                    <p className="text-sm text-isk-muted">Room Type: <span className="font-semibold text-isk-teal">{formData.room_type}</span></p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="description">Deskripsi *</Label>
                  <Textarea
                    id="description"
                    required
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Contoh: Pembayaran kamar 101 Februari"
                    rows={3}
                    className="glass-input border-2 focus:border-isk-teal rounded-xl resize-none"
                    data-testid="input-description"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="amount">Nominal (Rp) *</Label>
                  <Input
                    id="amount"
                    type="number"
                    required
                    min="0"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="2000000"
                    className="glass-input border-2 focus:border-isk-teal rounded-xl"
                    data-testid="input-amount"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Metode Pembayaran</Label>
                  <Select
                    value={formData.payment_method}
                    onValueChange={(value) => setFormData({ ...formData, payment_method: value })}
                  >
                    <SelectTrigger className="glass-input border-2 focus:border-isk-teal rounded-xl" data-testid="select-payment">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="transfer">Transfer</SelectItem>
                      <SelectItem value="other">Lainnya</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-isk-teal hover:bg-isk-dark text-white rounded-full py-6 font-semibold shadow-lg"
                  data-testid="submit-transaction"
                >
                  Simpan Transaksi
                </Button>
              </form>
            </div>
          </div>

          {/* Transactions List */}
          <div className="lg:col-span-2">
            {/* Filters */}
            <div className="glass-card rounded-2xl p-6 mb-6">
              <h3 className="text-xl font-bold text-isk-text mb-4 flex items-center">
                <Filter className="w-5 h-5 mr-2" />
                Filter Transaksi
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Dari Tanggal</Label>
                  <Input
                    type="date"
                    value={filterStartDate}
                    onChange={(e) => setFilterStartDate(e.target.value)}
                    className="glass-input border-2 focus:border-isk-teal rounded-xl"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Sampai Tanggal</Label>
                  <Input
                    type="date"
                    value={filterEndDate}
                    onChange={(e) => setFilterEndDate(e.target.value)}
                    className="glass-input border-2 focus:border-isk-teal rounded-xl"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Jenis</Label>
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger className="glass-input border-2 focus:border-isk-teal rounded-xl">
                      <SelectValue placeholder="Semua" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Semua</SelectItem>
                      <SelectItem value="income">Income</SelectItem>
                      <SelectItem value="expense">Expense</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Kategori</Label>
                  <Select value={filterCategory} onValueChange={setFilterCategory}>
                    <SelectTrigger className="glass-input border-2 focus:border-isk-teal rounded-xl">
                      <SelectValue placeholder="Semua" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Semua</SelectItem>
                      {[...incomeCategories, ...expenseCategories].map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Room</Label>
                  <Select value={filterRoom} onValueChange={setFilterRoom}>
                    <SelectTrigger className="glass-input border-2 focus:border-isk-teal rounded-xl">
                      <SelectValue placeholder="Semua" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Semua</SelectItem>
                      {rooms.map(room => (
                        <SelectItem key={room.room_number} value={room.room_number}>
                          {room.room_number}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-end">
                  <Button
                    onClick={handleFilter}
                    className="w-full bg-isk-teal hover:bg-isk-dark text-white rounded-xl py-6"
                    data-testid="apply-filter"
                  >
                    Terapkan Filter
                  </Button>
                </div>
              </div>

              <div className="mt-4 p-3 rounded-xl bg-isk-teal/5 border border-isk-teal/20">
                <p className="text-sm text-isk-muted">
                  Menampilkan <span className="font-bold text-isk-teal">{transactions.length}</span> transaksi
                </p>
              </div>
            </div>

            {/* Transactions Table */}
            <div className="glass-card rounded-2xl p-6">
              <h3 className="text-xl font-bold text-isk-text mb-6">Riwayat Transaksi</h3>

              <div className="overflow-x-auto">
                <table className="w-full" data-testid="transactions-table">
                  <thead>
                    <tr className="border-b border-white/20">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-isk-text">Tanggal</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-isk-text">Jenis</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-isk-text">Kategori</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-isk-text">Room</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-isk-text">Deskripsi</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-isk-text">Nominal</th>
                      <th className="text-center py-3 px-4 text-sm font-semibold text-isk-text">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.length > 0 ? (
                      transactions.map((trans, index) => (
                        <tr key={index} className="border-b border-white/10 hover:bg-white/30 transition-colors">
                          <td className="py-3 px-4 text-sm text-isk-muted">{formatDate(trans.transaction_date)}</td>
                          <td className="py-3 px-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              trans.transaction_type === 'income' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {trans.transaction_type === 'income' ? 'Income' : 'Expense'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-isk-text">{getCategoryLabel(trans.category, trans.transaction_type)}</td>
                          <td className="py-3 px-4 text-sm text-isk-muted">{trans.room_number || '-'}</td>
                          <td className="py-3 px-4 text-sm text-isk-muted">{trans.description}</td>
                          <td className={`py-3 px-4 text-sm font-bold text-right ${
                            trans.transaction_type === 'income' ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {formatCurrency(trans.amount)}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Button
                              onClick={() => handleDelete(trans)}
                              variant="outline"
                              size="sm"
                              className="border-red-300 text-red-600 hover:bg-red-600 hover:text-white"
                              data-testid={`delete-transaction-${index}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="py-8 text-center text-isk-muted">
                          Belum ada transaksi
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinanceEntry;
